/** Opt-in Hero backgrounds only; intentionally independent of shared media.js. */
class HeroVideo extends HTMLElement {
  connectedCallback() {
    if (this.controller || !this.closest('[data-hero-video-v1]')) return;
    this.controller = new AbortController();
    this.breakpoint = matchMedia('(width < 750px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.button = this.querySelector('[data-hero-video-toggle]');
    this.poster = this.querySelector('[data-hero-video-poster]');
    this.template = this.querySelector('template');
    this.visible = false;
    this.manualPlay = false;
    this.userPaused = this.hasAttribute('data-user-paused');
    this.failed = false;
    this.generation = 0;
    if (!this.button || !this.template) {
      this.controller.abort();
      this.controller = null;
      return;
    }
    // An editor DOM clone can contain a previous runtime child, unlike fresh Liquid HTML.
    this.querySelectorAll(':scope > video').forEach((video) => {
      video.pause();
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.removeAttribute('src');
      video.load();
      video.remove();
    });
    if (this.poster) this.poster.hidden = false;
    const signal = this.controller.signal;
    this.button.hidden = false;
    this.button.addEventListener('click', () => this.toggle(), { signal });
    this.breakpoint.addEventListener('change', () => {
      // Release every old-device instance before any new-device instance loads.
      for (const player of this.closest('[data-hero-video-v1]').querySelectorAll('hero-video')) {
        if (!player.deviceMatches()) player.release();
      }
      this.sync();
    }, { signal });
    this.motion.addEventListener('change', () => {
      this.manualPlay = false;
      if (this.motion.matches) this.release();
      this.sync();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.sync();
    });
    this.observer.observe(this);
    this.setState('idle');
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.release();
    if (this.button) this.button.hidden = true;
  }

  deviceMatches() {
    return this.dataset.device === 'both' || this.dataset.device === (this.breakpoint.matches ? 'mobile' : 'desktop');
  }

  sync() {
    if (!this.isConnected || !this.controller) return;
    if (!this.deviceMatches()) {
      this.release();
      return;
    }
    if (document.hidden || !this.visible || this.userPaused || (this.motion.matches && !this.manualPlay)) {
      clearTimeout(this.loadTimer);
      // Cancel pending playback too: an old promise must not block a later resume.
      if (this.video && !this.hasFrame) this.release();
      else this.video?.pause();
      return;
    }
    if (!this.failed) this.play();
  }

  toggle() {
    if (this.video && (!this.video.paused || this.pending)) {
      this.userPaused = true;
      this.setAttribute('data-user-paused', '');
      // Preserve user intent across device siblings in the same media slot.
      for (const player of this.closest('[data-hero-video-v1]').querySelectorAll('hero-video')) {
        if (player.dataset.slot === this.dataset.slot) {
          player.userPaused = true;
          player.setAttribute('data-user-paused', '');
        }
      }
      this.sync();
      return;
    }
    this.userPaused = false;
    for (const player of this.closest('[data-hero-video-v1]').querySelectorAll('hero-video')) {
      if (player.dataset.slot === this.dataset.slot) {
        player.userPaused = false;
        player.removeAttribute('data-user-paused');
      }
    }
    this.manualPlay = true;
    this.failed = false;
    if (this.deviceMatches()) this.play();
  }

  play() {
    if (this.pending || (this.video && !this.video.paused)) return;
    if (!this.video) {
      const video = this.template.content.querySelector('video')?.cloneNode(true);
      if (!video) return;
      this.video = video;
      video.muted = true;
      this.mediaController = new AbortController();
      const signal = this.mediaController.signal;
      video.addEventListener('playing', () => {
        if (this.video !== video) return;
        const revealFrame = () => {
          this.frameRequest = null;
          if (this.video !== video) return;
          if (!this.deviceMatches() || document.hidden || !this.visible || this.userPaused || (this.motion.matches && !this.manualPlay)) {
            this.sync();
            return;
          }
          this.hasFrame = true;
          clearTimeout(this.loadTimer);
          this.setState('playing');
          if (this.poster) this.poster.hidden = true;
        };
        // Keep the cover until a decoded frame is ready, not just a play promise.
        if (video.requestVideoFrameCallback) {
          if (this.frameRequest != null) video.cancelVideoFrameCallback(this.frameRequest);
          this.frameRequest = video.requestVideoFrameCallback(revealFrame);
        } else if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          revealFrame();
        }
      }, { signal });
      video.addEventListener('pause', () => {
        if (this.video === video && this.hasFrame && this.dataset.state !== 'failed') this.setState('paused');
      }, { signal });
      const fail = () => {
        if (this.video !== video) return;
        this.failed = true;
        this.release();
        this.setState('failed');
      };
      this.failMedia = fail;
      video.addEventListener('error', fail, { signal });
      const sources = [...video.querySelectorAll('source')];
      const failedSources = new Set();
      for (const source of sources) {
        // Source errors do not bubble to <video> on all browsers.
        source.addEventListener('error', () => {
          failedSources.add(source);
          if (failedSources.size === sources.length) fail();
        }, { signal });
      }
      // A hung connection must not leave a permanent loading state.
      this.append(video);
      this.setState('loading');
    }
    const video = this.video;
    const generation = this.generation;
    if (!this.hasFrame) this.loadTimer = setTimeout(this.failMedia, 10000);
    this.pending = true;
    video.play().catch((error) => {
      if (generation !== this.generation || this.video !== video) return;
      // A deliberate background/offscreen pause is not a failed autoplay attempt.
      if (error.name === 'AbortError' && (document.hidden || !this.visible || this.userPaused)) return;
      this.failed = true;
      this.release();
      this.setState('blocked');
    }).finally(() => {
      if (generation === this.generation) this.pending = false;
    });
  }

  release() {
    clearTimeout(this.loadTimer);
    this.generation = (this.generation ?? 0) + 1;
    this.pending = false;
    const video = this.video;
    this.video = null;
    if (video && this.frameRequest != null) video.cancelVideoFrameCallback?.(this.frameRequest);
    this.frameRequest = null;
    this.mediaController?.abort();
    this.mediaController = null;
    this.failMedia = null;
    this.hasFrame = false;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
      video.remove();
    }
    if (this.poster) this.poster.hidden = false;
    this.setState('idle');
  }

  setState(state) {
    this.dataset.state = state;
    if (!this.button) return;
    const canPause = state === 'playing' || state === 'loading';
    const label = canPause ? this.dataset.pauseLabel : this.dataset.playLabel;
    this.button.setAttribute('aria-label', label);
    const text = this.button.querySelector('[data-hero-video-label]');
    if (text) text.textContent = label;
    this.button.querySelector('[data-icon-play]')?.toggleAttribute('hidden', canPause);
    this.button.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !canPause);
  }
}

if (!customElements.get('hero-video')) customElements.define('hero-video', HeroVideo);
